import { useApp } from '@/App';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import {
	Pipeline,
	ProspectSteps,
	getPipelineDetail,
} from '@/libs/fleet-api/services/pipeline';
import { LoadingOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Col, Divider, Form, Result, Row, Table } from 'antd';
import { FC, useState } from 'react';
import PipelineMainInformation from '../pipeline/PipelineMainInformation';

export type PipelineModal = null | {
	pipelineId: number;
};

type PipelineModalProps = {
	modal: PipelineModal;
	onClose: () => void;
};

const PipelineModal: FC<PipelineModalProps> = ({ modal, onClose }) => {
	const { navigate } = useApp();

	const [successSaveModal, setSuccessSaveModal] = useState<boolean>(false);

	const { form } = useForm<Pipeline<'payload'>>();

	const { data, isLoading } = useQuery({
		queryKey: ['getPipelineDetail', modal?.pipelineId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getPipelineDetail(id);
		},
		enabled: !!modal?.pipelineId,
	});

	if (isLoading) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	if (!data) return;

	const {
		pipelineApprovalHistory: approvalHistory,
		customerDocuments,
		pipelineDetail,
	} = data.data;

	return (
		<FleetModal
			open={Boolean(modal)}
			width={940}
			cancelText="BACK"
			buttonPosition="end"
			closable={false}
			okButtonProps={{
				hidden: true,
			}}
			onCancel={() => onClose?.()}
		>
			<FleetModal
				open={successSaveModal}
				okText="NEXT"
				onOk={() => {
					setSuccessSaveModal(false);
					navigate('FHPipelineListPage');
				}}
			>
				<Result
					status="success"
					title={<div style={{ fontSize: 30 }}>Sukses Menyetujui</div>}
				/>
			</FleetModal>
			<PipelineMainInformation
				mainInformation={{
					prospectId: pipelineDetail.prospectId,
					prospectStatus: pipelineDetail.prospectStatus?.value,
					orderSource: pipelineDetail.orderSource?.value,
					customerId: pipelineDetail.customerId,
					customerName: pipelineDetail.customerName,
					customerGroup: pipelineDetail.customerGroup ?? { id: 0, value: '' },
					industrySector: pipelineDetail.industrySector ?? { id: 0, value: '' },
					slikResult: '',
					frm: { id: 0, value: '' },
					creditAnalyst: { id: 0, value: '' },
					existingPlafond: pipelineDetail.existingFinancingLimit ?? 0,
					appliedPlafond: pipelineDetail.limitApplication ?? 0,
					applicationStatus: pipelineDetail.prospectStatus?.value,
					newOrderDate: pipelineDetail.newOrderDate,
					outstandingPrincipal: pipelineDetail?.outstandingPrincipal ?? 0,
				}}
				customerBusinessDocuments={customerDocuments.customerBusinessDocuments}
				customerLegalDocuments={customerDocuments.customerLegalDocuments}
				customerReferralDocuments={customerDocuments.customerReferralDocuments}
				isLoading={isLoading}
				prospectStatusId={
					(pipelineDetail.prospectStatus.id ??
						ProspectSteps.Bch) as unknown as ProspectSteps
				}
			/>

			<Divider />
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				name="productOffer"
			>
				<Row gutter={[20, 40]}>
					<Col
						span={5}
						style={{ fontWeight: 600, fontSize: 14 }}
					>
						Ultimate Owner
					</Col>
					<Col
						span={19}
						style={{ marginLeft: -10 }}
					>
						{pipelineDetail?.ultimateOwner}
					</Col>
				</Row>

				<Divider />

				<Row gutter={[20, 40]}>
					<Col
						span={5}
						style={{ fontWeight: 600, fontSize: 14 }}
					>
						Current Issue
					</Col>
					<Col
						span={19}
						style={{ marginLeft: -10 }}
					>
						{pipelineDetail?.currentIssue}
					</Col>
				</Row>

				<Divider />

				<Row gutter={[20, 40]}>
					<Col
						span={5}
						style={{ fontWeight: 600, fontSize: 14 }}
					>
						Skema Kredit
					</Col>
					<Col
						span={19}
						style={{ marginLeft: -10 }}
					>
						{pipelineDetail?.creditScheme}
					</Col>
				</Row>

				<Divider />

				<Row gutter={[20, 40]}>
					<Col
						span={5}
						style={{ fontWeight: 600, fontSize: 14 }}
					>
						Review Credit
					</Col>
					<Col
						span={19}
						style={{ marginLeft: -10 }}
					>
						{pipelineDetail.creditReview}
					</Col>
				</Row>

				<Divider />

				<Row gutter={[20, 40]}>
					<Col
						span={5}
						style={{ fontWeight: 600, fontSize: 14 }}
					>
						Rekomendasi Credit
					</Col>
					<Col
						span={19}
						style={{ marginLeft: -10 }}
					>
						{pipelineDetail.creditRecommendation?.value}
					</Col>
				</Row>

				<Divider />

				<FleetTitle>Approval History</FleetTitle>
				<Table
					bordered
					loading={isLoading}
					pagination={false}
					dataSource={
						approvalHistory?.map((approval, key) => {
							return {
								key,
								...approval,
							};
						}) ?? []
					}
					columns={[
						{
							title: 'No',
							render: (_, record) => record.no,
						},
						{
							title: 'Name',
							render: (_, record) => record.approvalBy,
						},
						{
							title: 'Date',
							render: (_, record) => record.approvalDate,
						},
						{
							title: 'Process',
							render: (_, record) => record.approvalProcess,
						},
						{
							title: 'Status',
							align: 'center',
							render: (_, record) => {
								return (
									<span
										className={`review-status ${record.approvalStatus.toLowerCase()}`}
									>
										{record.approvalStatus}
									</span>
								);
							},
						},
						{
							title: 'Approval Notes',
							render: (_, record) => record.approvalNote,
						},
					]}
					style={{ marginBottom: 20 }}
				/>
			</Form>
			<Divider />
		</FleetModal>
	);
};

export default PipelineModal;

export const usePipelineModal = () => {
	return useState<PipelineModal>(null);
};
